import {
    appendModalControllerIfRequired,
    makePopoverHandler,
    debounce,
} from '../../global/helpers.js';
import { Labels } from '../../global/labels.js';
import {
    addOutsideClickHandler,
    addClickPropagationStopper,
    element,
    addMultipleEventListener,
    getChildrenForSlot,
    keepAllowedElements,
    addContentShiftHandler,
} from '../../global/ui-helpers.js';
import { UIElement } from '../ui-element.js';
import { TabIndex } from '../../global/keyboard.js';
import { UIIcon } from '../icon/ui-icon.js';
import styles from './ui-annotation.css';
import { calculateTooltipOffset, Digest } from '../../global/index.js';
import { browser } from '../../global/browser.js';
import { eventBus } from '../../global/event-bus.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAnnotation
 * @element ui-annotation
 * @property {string} [labelClose] {@attr label-close} Label for close button.
 * @property {HTMLButtonElement} closeButton Shortcut to close button.
 * @classdesc Represents a class for <code>ui-annotation</code> element.
 * @slot {@name title} Title of the annotation.
 * @slot {@name message} Message of the annotation.
 * @slot {@name action} Action element/s of the annotation, maximum of two elements.
 * @example
 * <ui-badge layout="pin">Badge
 *      <ui-annotation expanded>
 *          <p slot="message">It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English.</p>
 *          <ui-chip label="Action" layout="button"></ui-chip>
 *          <ui-chip label="Action" layout="button"></ui-chip>
 *      </ui-annotation>
 *  </ui-badge>
 */
class UIAnnotation extends UIElement {
    /**
     * State: Determines whether annotation can be auto-opened, e.g. on parent focus.
     * Needed to prevent auto-opening again right after it has been closed, so focus remains on parent element.
     * @type {boolean}
     */
    static preventAutoFocus = false;

    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                expanded: Boolean,
                labelClose: { type: String, default: '' },
            },
            children: {
                closeButton: '.ui-annotation__close-button',
            },
        };
    }

    /**
     * Provides list of observed attributes to be watched.
     * @type {Array<string>}
     * @readonly
     */
    static get observedAttributes() {
        return ['expanded'];
    }

    /**
     * Define labels what could be localised.
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-annotation', {
            close: 'Close annotation',
        });
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get allowedHeadingElementTags() {
        return ['h2', 'h3', 'h4', 'h5', 'h6'];
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get allowedMessageElementTags() {
        return ['p', 'ul', 'ol'];
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get allowedActionElementTags() {
        return ['ui-chip'];
    }

    /**
     * @type {HTMLElement}
     * @readonly
     */
    get popoverElement() {
        if (this.detachedPopover) {
            return this.detachedPopover;
        }
        return this.querySelector('.ui-annotation__popover');
    }

    /**
     * Resolves closing label for screen-readers.
     * @returns {string}
     * @private
     */
    getAccessibilityCloseLabel() {
        return this.labelClose || UIAnnotation.labels.close;
    }

    /**
     * Extracts the elements for the given slot.
     * @param {string} slotName
     * @param {Array<string>} allowedElementTags
     * @returns {Array<HTMLElement>}
     * @private
     */
    getElements(slotName, allowedElementTags) {
        const childrenForSlot = getChildrenForSlot(this, slotName);
        const slotNodes = keepAllowedElements(
            childrenForSlot,
            allowedElementTags
        );

        if (
            UIAnnotation.DEBUG_MODE &&
            childrenForSlot.length !== slotNodes.length
        ) {
            const coloredTags = allowedElementTags
                .map((tag) => `%c${tag}%c`)
                .toString();
            const coloredTagsStyles = allowedElementTags.flatMap(() => [
                'color: red',
                '',
            ]);
            console.warn(
                `Tooltip %c${slotName}%c allows only ${coloredTags} element!`,
                'color: blue',
                '',
                ...coloredTagsStyles
            );
        }

        return slotNodes;
    }

    /**
     * @fires event:modal-open
     * @private
     */
    openDetachedPopover() {
        if (this.detachedPopover || !this.parentElement) {
            return;
        }

        const { container, pointer } = calculateTooltipOffset(
            this.parentElement,
            this.popoverElement
        );

        this.popoverElement.style.setProperty(
            '--ui-annotation-pointer-horizontal-offset',
            `${pointer.x}px`
        );
        this.popoverElement.style.setProperty(
            '--ui-annotation-pointer-vertical-offset',
            `${pointer.y}px`
        );
        this.popoverElement.style.setProperty(
            '--ui-annotation-pointer-angle',
            `${pointer.rotation}deg`
        );
        this.popoverElement.style.setProperty(
            '--ui-annotation-pointer-hover-area-width',
            `${browser.hoverCapableMediaMatches() ? pointer.hoverAreaWidth : 0}px`
        );
        this.popoverElement.style.setProperty(
            '--ui-annotation-max-width',
            `${container.width}px`
        );

        const popoverHandler = makePopoverHandler(this.parentElement, {
            popoverHeight: container.height,
            verticalOffset: container.y,
            popoverWidth: container.width,
            horizontalOffset: container.x,
            supportMiddleAlignment: false,
        });
        this.detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'popover',
            content: this.popoverElement,
            params: {
                position: () => {
                    return popoverHandler();
                },
                target: this,
                onOpen: () => {
                    this.popoverElement && this.popoverElement.focus();
                },
                onClose: () => {
                    this.parentElement.focus();
                    this.expanded = false;
                },
            },
        });
    }

    /**
     * @returns {UIElement | null}
     * @private
     */
    closeDetachedPopover() {
        if (!this.detachedPopover) {
            return null;
        }

        const modal = this.detachedPopover.closest('ui-modal');
        if (modal) {
            this.detachedPopover = null;
            modal.close();
        }

        return modal;
    }

    /**
     * Controls the popover visibility.
     * @private
     */
    onPopoverStateChange() {
        if (!this.popoverElement) {
            return;
        }

        if (this.expanded) {
            this.openDetachedPopover();
            addOutsideClickHandler(
                [this, this.popoverElement, this.parentElement],
                this.closePopoverHandler
            ).then((func) => (this.outsideClickRemover = func));
            this.removeClickPropagationStopper = addClickPropagationStopper(
                this.popoverElement,
                'Tooltip click propagation stopped!'
            );
        } else {
            this.closeDetachedPopover();
            this.outsideClickRemover?.();
            this.removeOutsideClickHandler = null;
            this.removeClickPropagationStopper?.();
            this.removeClickPropagationStopper = null;
            this.removeContentShiftHandler?.();
            this.removeContentShiftHandler = null;
        }
    }

    /**
     * @private
     */
    onOpenPopover() {
        this.expanded = true;
    }

    /**
     * @param {number | undefined} delay
     * @private
     */
    onClosePopover(delay = 0) {
        if (this.cancelClosePopover) {
            return;
        }

        const closePopover = () => {
            this.cancelClosePopover = null;
            this.expanded = false;
            if (
                !document.activeElement ||
                document.activeElement === document.body
            ) {
                this.parentElement.focus();
            }
        };

        const debouncedClosePopover = debounce(closePopover.bind(this), delay);
        this.cancelClosePopover = debouncedClosePopover.cancel;
        debouncedClosePopover();
    }

    /**
     * @private
     */
    onTogglePopover() {
        this.expanded = !this.expanded;
    }

    /**
     * @private
     */
    onPointerEnter() {
        if (!this.cancelClosePopover) {
            this.openPopoverHandler();
            return;
        }

        this.cancelClosePopover();
        this.cancelClosePopover = null;
    }

    /**
     * @private
     */
    onPointerLeave() {
        this.closePopoverHandler(100);
    }

    /**
     * Adjusts event handlers in the event of device hover capability change.
     * @private
     */
    onHoverCapabilityChange() {
        this.removeDynamicPopupTriggerHandlers();
        this.addDeviceDependantEventListeners();
        this.onResize();
    }

    /**
     * @private
     */
    onResize() {
        this.popoverElement.style.setProperty(
            '--ui-annotation-max-width',
            `360px`
        );
        if (this.detachedPopover) {
            const modal = this.closeDetachedPopover();

            if (modal) {
                const modalUpdateHandler = () => {
                    this.openDetachedPopover();
                    modal?.removeEventListener(
                        'modal-updatelist',
                        modalUpdateHandler
                    );
                };

                modal.addEventListener('modal-updatelist', modalUpdateHandler);
            }
        }
    }

    /**
     * Util for adding device hover capability related event handlers.
     * @private
     */
    addDeviceDependantEventListeners() {
        if (browser.hoverCapableMediaMatches()) {
            const removeOpenPopoverFromParentElementHandler =
                addMultipleEventListener(
                    this.parentElement,
                    'pointerenter',
                    this.pointerEnterHandler
                );
            const removeClosePopoverFromParentElementHandler =
                addMultipleEventListener(
                    this.parentElement,
                    'pointerleave',
                    this.pointerLeaveHandler
                );
            const removeOpenPopoverFromPopoverElementHandler =
                addMultipleEventListener(
                    this.popoverElement,
                    'pointerenter',
                    this.pointerEnterHandler
                );
            const removeClosePopoverFromPopoverElementHandler =
                addMultipleEventListener(
                    this.popoverElement,
                    'pointerleave',
                    this.pointerLeaveHandler
                );

            this.removeDynamicPopupTriggerHandlers = () => {
                removeOpenPopoverFromParentElementHandler();
                removeClosePopoverFromParentElementHandler();
                removeOpenPopoverFromPopoverElementHandler();
                removeClosePopoverFromPopoverElementHandler();
            };
            return;
        }

        const removeTogglePopoverFromParentElementHandler =
            addMultipleEventListener(
                this.parentElement,
                'click',
                this.togglePopoverHandler
            );
        this.removeDynamicPopupTriggerHandlers = () => {
            removeTogglePopoverFromParentElementHandler();
        };
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    addEventListeners() {
        this.addDeviceDependantEventListeners();
        browser.addHoverCapableChangeListener(
            this.hoverCapabilityChangeHandler
        );
        this.closeButton.addEventListener('click', this.closePopoverHandler);
        window.addEventListener('resize', this.resizeHandler);
        this.parentElement.addEventListener(
            'focus',
            this.focusAutoOpenPopoverHandler
        );
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    removeEventListeners() {
        this.removeDynamicPopupTriggerHandlers();
        browser.removeHoverCapableChangeListener(
            this.hoverCapabilityChangeHandler
        );
        this.closeButton.removeEventListener('click', this.closePopoverHandler);
        window.removeEventListener('resize', this.resizeHandler);
        this.parentElement.removeEventListener(
            'focus',
            this.focusAutoOpenPopoverHandler
        );
    }

    /**
     * Automatically open popover on some event, e.g. when parent is focused.
     *  Example use case:
     *  1) when parent gets focus, popover will be toggled to be visible.
     *     if popover is already visible, parent can't be focused as focus is
     *     already on the popover.
     *  2) focus gets trapped inside popover until closed
     *  3) focus returns to parent when popover is closed, popover remains invisible
     *  4) if focus is moved away from parent and back to it, popover auto-opens again.
     * @private
     */
    onFocusAutoOpenPopover() {
        // If autofocus is disabled, enable it again.
        if (this.preventAutoFocus) {
            this.preventAutoFocus = false;
            return;
        }
        // If autofocus is allowed, open popover and disable autofocus
        // so when closed, focus would stay on the parent not open the popover again
        this.openPopoverHandler();
        this.preventAutoFocus = true;
    }

    /**
     * Util for waiting for the Roboto font to be loaded and modal controller to have state hydrated and then triggers the annotation.
     * @returns {Promise<void>}
     * @private
     */
    async renderInitiallyExpanded() {
        await document.fonts.load(`1em RobotoRegular`);

        const modalController = document.querySelector('ui-modal-controller');

        if (modalController.state !== 'hydrated') {
            const onModalControllerAttached = () => {
                eventBus.removeEventListener(
                    'modal-controller-ready',
                    onModalControllerAttached
                );
                this.onPopoverStateChange();
                this.removeContentShiftHandler = addContentShiftHandler(
                    this,
                    this.onResize.bind(this)
                );
            };

            eventBus.addEventListener(
                'modal-controller-ready',
                onModalControllerAttached
            );
            return;
        }

        this.onPopoverStateChange();
        this.removeContentShiftHandler = addContentShiftHandler(
            this,
            this.onResize.bind(this)
        );
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name, oldValue, newValue) {
        if (this.state !== 'hydrated') {
            return;
        }

        switch (name) {
            case 'expanded':
                this.onPopoverStateChange();
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render() {
        const titleElement = this.getElements(
            'title',
            UIAnnotation.allowedHeadingElementTags
        )[0];
        const messageElements = this.getElements(
            'message',
            UIAnnotation.allowedMessageElementTags
        );
        const actionElements = this.getElements(
            'action',
            UIAnnotation.allowedActionElementTags
        ).slice(0, 2);
        this.innerHTML = '';
        const popoverId = Digest.randomId();

        UIAnnotation.DEBUG_MODE &&
            messageElements.length === 0 &&
            console.warn(
                'Tooltip %cmessage%c is mandatory!',
                'color: blue',
                ''
            );

        this.append(
            element`
                <div
                  id="${popoverId}"
                  class="ui-annotation__popover"
                  tabindex="${TabIndex.Active}"
                >
                    <div class="ui-annotation__content">
                      ${titleElement}
                      ${messageElements.length > 0 ? messageElements : element`<p>Message is mandatory!</p>`}
                      ${actionElements.length > 0 ? element`<div class="ui-annotation__action-buttons-wrapper">${actionElements}</div>` : ''}
                    </div>
                    <div class="ui-annotation__pointer-wrapper">
                      <div class="ui-annotation__pointer"></div>
                    </div>
                    <button
                      class="ui-annotation__close-button -iconed"
                      type="button"
                      title="${this.getAccessibilityCloseLabel()}"
                    >
                        <ui-icon
                          class="-navicon"
                          glyph="redesign-x-mark"
                          color="${UIIcon.colors.DEFAULT}"
                        ></ui-icon>
                    </button>
                </div>
          `
        );
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        appendModalControllerIfRequired();
        this.openPopoverHandler = this.onOpenPopover.bind(this);
        this.closePopoverHandler = this.onClosePopover.bind(this);
        this.togglePopoverHandler = this.onTogglePopover.bind(this);
        this.focusAutoOpenPopoverHandler =
            this.onFocusAutoOpenPopover.bind(this);
        this.hoverCapabilityChangeHandler =
            this.onHoverCapabilityChange.bind(this);
        this.pointerEnterHandler = this.onPointerEnter.bind(this);
        this.pointerLeaveHandler = this.onPointerLeave.bind(this);
        this.resizeHandler = debounce(this.onResize.bind(this), 200);
        this.addEventListeners();
        if (this.expanded) {
            this.renderInitiallyExpanded();
        }
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.closeDetachedPopover();
        this.removeEventListeners();
        this.outsideClickRemover?.();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.addEventListeners();
    }
}

UIAnnotation.defineElement('ui-annotation', styles);
export { UIAnnotation };
